import { useEffect, useMemo, useRef, useState } from "react";
import api, { openPdf } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Save, Trash2, Copy, Printer, Eye, QrCode, Barcode as BarcodeIcon, Image as ImageIcon, Sparkles, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import LabelThumbnail from "../components/LabelThumbnail";

/**
 * Etiketten-Designer (Runde 114).
 * Drag-Positionierung im Preview-Canvas, Feld-Bibliothek, Styling-Panel,
 * Standard-Größen + manuelle Eingabe, Vorlagen-Speicherung/-Laden, PDF-Export.
 */
const PRESETS = [
  { key: "dhl", label: "DHL 100 × 150 mm", w: 100, h: 150 },
  { key: "artikel62", label: "Artikeletikett 62 × 29 mm", w: 62, h: 29 },
  { key: "klein", label: "Klein 40 × 30 mm", w: 40, h: 30 },
  { key: "artikel50", label: "Artikeletikett 50 × 25 mm", w: 50, h: 25 },
];

const FIELD_TYPES = [
  { type: "logo", label: "POPCRN Logo", h: 12, w: 24 },
  { type: "sku", label: "SKU", fs: 10, sample: "POP-K-500" },
  { type: "item_name", label: "Artikelname", fs: 12, bold: true, sample: "Karamell 500 g" },
  { type: "barcode", label: "Barcode/EAN", h: 15 },
  { type: "charge_number", label: "Chargennr.", fs: 9, sample: "2026-02-01-01" },
  { type: "mhd", label: "MHD", fs: 9, sample: "MHD 31.12.2026" },
  { type: "location", label: "Lagerplatz", fs: 9, sample: "A-01-03" },
  { type: "quantity", label: "Menge", fs: 10, sample: "24 Stk." },
  { type: "customer_name", label: "Kunde", fs: 11, sample: "Musterkunde GmbH" },
  { type: "order_number", label: "Bestellnr.", fs: 10, sample: "AB-2026-0042" },
  { type: "delivery_date", label: "Lieferdatum", fs: 9, sample: "05.03.2026" },
  { type: "price", label: "Preis", fs: 12, bold: true, sample: "12,90 €" },
  { type: "text", label: "Freier Text", fs: 10 },
];

const FIELD_META = Object.fromEntries(FIELD_TYPES.map((f) => [f.type, f]));

const MM_TO_PX = 3.7795; // 96 DPI ≈ 3.78 px/mm
const CANVAS_ZOOM = 3;   // Preview scale factor for readability

export default function LabelDesigner() {
  const [templates, setTemplates] = useState([]);
  const [tpl, setTpl] = useState({
    id: null, name: "Neue Vorlage", width_mm: 62, height_mm: 29,
    background_color: "#ffffff", fields: [],
  });
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [busy, setBusy] = useState(false);
  const canvasRef = useRef(null);
  const drag = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/label-templates");
      setTemplates(data || []);
    } catch { setTemplates([]); }
  };
  useEffect(() => { load(); }, []);

  const px = MM_TO_PX * CANVAS_ZOOM;

  const selectPreset = (p) => setTpl({ ...tpl, width_mm: p.w, height_mm: p.h });

  const addField = (type) => {
    const meta = FIELD_TYPES.find((f) => f.type === type) || {};
    const defaultW = meta.w || Math.min(30, tpl.width_mm - 4);
    const f = {
      type, x: 2, y: 2 + tpl.fields.length * 6,
      width: type === "barcode" ? Math.min(30, tpl.width_mm - 4) : defaultW,
      height: meta.h || 6,
      font_size: meta.fs || 10, bold: !!meta.bold, italic: false,
      align: "left", border: false,
      text: type === "text" ? "Freier Text" : undefined,
      barcode_type: type === "barcode" ? "code128" : undefined,
    };
    setTpl({ ...tpl, fields: [...tpl.fields, f] });
    setSelectedIdx(tpl.fields.length);
  };

  const updateField = (idx, patch) => {
    const arr = tpl.fields.slice();
    arr[idx] = { ...arr[idx], ...patch };
    setTpl({ ...tpl, fields: arr });
  };

  const removeField = (idx) => {
    setTpl({ ...tpl, fields: tpl.fields.filter((_, i) => i !== idx) });
    setSelectedIdx(-1);
  };

  const onDown = (e, idx) => {
    e.preventDefault();
    setSelectedIdx(idx);
    const f = tpl.fields[idx];
    const rect = canvasRef.current.getBoundingClientRect();
    drag.current = {
      idx,
      startX: e.clientX - rect.left - f.x * px,
      startY: e.clientY - rect.top - f.y * px,
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  const onMove = (e) => {
    if (!drag.current || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const nxRaw = (e.clientX - rect.left - drag.current.startX) / px;
    const nyRaw = (e.clientY - rect.top - drag.current.startY) / px;
    // Grid-Snap auf 1mm — mit Alt-Taste pixelgenau (Runde 115)
    const nx = e.altKey ? nxRaw : Math.round(nxRaw);
    const ny = e.altKey ? nyRaw : Math.round(nyRaw);
    const idx = drag.current.idx;
    setTpl((cur) => {
      const arr = cur.fields.slice();
      const f = arr[idx];
      arr[idx] = { ...f,
        x: Math.max(0, Math.min(cur.width_mm - f.width, nx)),
        y: Math.max(0, Math.min(cur.height_mm - f.height, ny)) };
      return { ...cur, fields: arr };
    });
  };
  const onUp = () => {
    drag.current = null;
    window.removeEventListener("mousemove", onMove);
    window.removeEventListener("mouseup", onUp);
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        name: tpl.name, width_mm: tpl.width_mm, height_mm: tpl.height_mm,
        background_color: tpl.background_color, fields: tpl.fields,
        category: tpl.category || null,
      };
      let res;
      if (tpl.id) res = await api.patch(`/label-templates/${tpl.id}`, body);
      else res = await api.post("/label-templates", body);
      toast.success(`Vorlage „${res.data.name}" gespeichert`);
      setTpl({ ...tpl, id: res.data.id });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const loadTemplate = (t) => {
    setTpl({
      id: t.id, name: t.name, width_mm: t.width_mm, height_mm: t.height_mm,
      background_color: t.background_color || "#ffffff", fields: t.fields || [],
      category: t.category || null,
    });
    setSelectedIdx(-1);
  };

  const duplicate = () => {
    const catLabel = tpl.category ? ` · ${tpl.category.charAt(0).toUpperCase()}${tpl.category.slice(1)}` : "";
    setTpl({ ...tpl, id: null, name: `${tpl.name} Kopie${catLabel}` });
    toast.info('Als Kopie – nicht gespeichert. Klicke „Speichern" für die neue Vorlage.');
  };

  const remove = async () => {
    if (!tpl.id) return;
    if (!window.confirm(`Vorlage „${tpl.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/label-templates/${tpl.id}`);
      toast.success("Vorlage gelöscht");
      setTpl({ id: null, name: "Neue Vorlage", width_mm: 62, height_mm: 29,
               background_color: "#ffffff", fields: [] });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const preview = async () => {
    if (!tpl.id) { toast.error("Erst speichern, dann Vorschau möglich"); return; }
    try { await openPdf(`/label-templates/${tpl.id}/preview.pdf`); }
    catch { toast.error("Vorschau fehlgeschlagen"); }
  };

  const seedDefaults = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/label-templates/seed-defaults");
      const c = data?.created?.length || 0;
      const s = data?.skipped?.length || 0;
      if (c > 0) toast.success(`${c} Standard-Vorlage${c === 1 ? "" : "n"} angelegt${s ? ` · ${s} vorhanden` : ""}`);
      else toast.info(`Alle ${s} Standard-Vorlagen sind bereits vorhanden.`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Seed fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const printA4Sheet = async () => {
    if (!tpl.id) { toast.error("Erst speichern, dann A4-Bogen drucken"); return; }
    const raw = window.prompt(`Wie viele Kopien der Vorlage „${tpl.name}" auf A4?`, "24");
    if (!raw) return;
    const count = Math.max(1, Math.min(500, parseInt(raw, 10) || 0));
    if (count <= 0) return;
    setBusy(true);
    try {
      const res = await api.post(`/label-templates/${tpl.id}/a4-sheet.pdf`,
        { count }, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "A4-Bogen fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const sel = selectedIdx >= 0 ? tpl.fields[selectedIdx] : null;

  return (
    <div data-testid="page-label-designer">
      <PageHeader
        eyebrow="Einstellungen"
        title="Etiketten-Designer"
        description="Erstelle eigene Etiketten-Vorlagen mit Drag-Positionierung und exakter mm-Größe. Nutze sie beim Drucken aus Lager, Versand oder Produktion."
      />

      {/* Top toolbar */}
      <div className="bg-white border border-[var(--border)] rounded-2xl p-3 mb-4 flex items-center gap-2 flex-wrap">
        <Select value={tpl.id || "__new"}
                onValueChange={(v) => {
                  if (v === "__new") setTpl({ id: null, name: "Neue Vorlage", width_mm: 62, height_mm: 29, background_color: "#ffffff", fields: [] });
                  else { const t = templates.find((x) => x.id === v); if (t) loadTemplate(t); }
                }}>
          <SelectTrigger className="w-56" data-testid="select-template"><SelectValue placeholder="Vorlage wählen" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__new">+ Neue Vorlage</SelectItem>
            {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.width_mm}×{t.height_mm}mm)</SelectItem>)}
          </SelectContent>
        </Select>
        <Input value={tpl.name} onChange={(e) => setTpl({ ...tpl, name: e.target.value })}
               data-testid="input-tpl-name" placeholder="Name der Vorlage" className="w-52" />
        <Select value=""
                onValueChange={(v) => { const p = PRESETS.find((x) => x.key === v); if (p) selectPreset(p); }}>
          <SelectTrigger className="w-56" data-testid="select-preset"><SelectValue placeholder="Standardgröße wählen" /></SelectTrigger>
          <SelectContent>
            {PRESETS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1 text-xs">
          <span className="text-[var(--text-muted)]">B×H mm:</span>
          <Input type="number" value={tpl.width_mm} onChange={(e) => setTpl({ ...tpl, width_mm: Number(e.target.value) || 62 })}
                 data-testid="input-width" className="w-16 h-8 text-xs" />
          ×
          <Input type="number" value={tpl.height_mm} onChange={(e) => setTpl({ ...tpl, height_mm: Number(e.target.value) || 29 })}
                 data-testid="input-height" className="w-16 h-8 text-xs" />
        </div>
        <Select value={tpl.category || "__all"}
                onValueChange={(v) => setTpl({ ...tpl, category: v === "__all" ? null : v })}>
          <SelectTrigger className="w-40" data-testid="select-category"><SelectValue placeholder="Kategorie" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">Ohne Kategorie</SelectItem>
            <SelectItem value="versand">Versand</SelectItem>
            <SelectItem value="artikel">Artikel</SelectItem>
            <SelectItem value="charge">Charge</SelectItem>
            <SelectItem value="produktion">Produktion</SelectItem>
            <SelectItem value="sonstiges">Sonstiges</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1 text-xs">
          <span className="text-[var(--text-muted)]">BG:</span>
          <input type="color" value={tpl.background_color} onChange={(e) => setTpl({ ...tpl, background_color: e.target.value })}
                 data-testid="input-bg-color" className="w-8 h-8 rounded border border-[var(--border)]" />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" onClick={seedDefaults} disabled={busy}
                  data-testid="btn-seed-defaults" className="rounded-full">
            <Sparkles size={14} className="mr-1" /> Standard-Vorlagen
          </Button>
          <Button variant="outline" onClick={printA4Sheet} disabled={!tpl.id || busy}
                  data-testid="btn-a4-sheet" className="rounded-full">
            <LayoutGrid size={14} className="mr-1" /> A4-Bogen
          </Button>
          <Button variant="outline" onClick={preview} disabled={!tpl.id}
                  data-testid="btn-preview" className="rounded-full">
            <Eye size={14} className="mr-1" /> Vorschau
          </Button>
          <Button variant="outline" onClick={duplicate} disabled={!tpl.id}
                  data-testid="btn-duplicate" className="rounded-full">
            <Copy size={14} className="mr-1" /> Duplizieren
          </Button>
          {tpl.id && (
            <Button variant="outline" onClick={remove}
                    className="rounded-full border-red-300 text-red-700 hover:bg-red-50">
              <Trash2 size={14} className="mr-1" /> Löschen
            </Button>
          )}
          <Button onClick={save} disabled={busy || !tpl.name}
                  data-testid="btn-save-tpl"
                  className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
            <Save size={14} className="mr-1" /> {busy ? "Speichere…" : "Speichern"}
          </Button>
        </div>
      </div>

      {/* Template thumbnail strip (Runde 124.6) — visual picker */}
      {templates.length > 0 && (
        <div className="bg-white border border-[var(--border)] rounded-2xl p-3 mb-4 overflow-x-auto" data-testid="template-strip">
          <div className="overline text-[10px] mb-2 flex items-center gap-1">
            <LayoutGrid size={11} /> Vorlagen ({templates.length})
          </div>
          <div className="flex items-start gap-3 min-w-max pb-1">
            {templates.map((t) => {
              const active = tpl.id === t.id;
              return (
                <button key={t.id}
                        onClick={() => loadTemplate(t)}
                        data-testid={`tpl-thumb-${t.id}`}
                        className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-all hover:bg-[var(--pop-cream)]/40 ${active ? "bg-[var(--pop-pink)]/10" : ""}`}
                        title={`${t.name} · ${t.width_mm}×${t.height_mm}mm · ${t.fields?.length || 0} Felder`}>
                  <LabelThumbnail tpl={t} width={96} height={60} active={active} />
                  <div className="text-[10px] font-semibold truncate max-w-[100px]">{t.name}</div>
                  <div className="text-[9px] font-mono text-[var(--text-muted)]">
                    {t.width_mm}×{t.height_mm} mm
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr_280px] gap-4">
        {/* Field palette */}
        <div className="bg-white border border-[var(--border)] rounded-2xl p-3 space-y-1" data-testid="palette">
          <div className="overline text-xs mb-2">Felder einfügen</div>
          {FIELD_TYPES.map((f) => (
            <button key={f.type} onClick={() => addField(f.type)}
                    data-testid={`add-${f.type}`}
                    className="w-full text-left px-3 py-2 rounded-lg text-xs font-semibold hover:bg-[var(--pop-cream)]/50 flex items-center gap-2 border border-transparent hover:border-[var(--border)]">
              {f.type === "barcode" ? <BarcodeIcon size={13} />
                : f.type === "logo" ? <ImageIcon size={13} />
                : <Plus size={13} />}
              {f.label}
            </button>
          ))}
        </div>

        {/* Canvas */}
        <div className="bg-[var(--pop-cream)]/20 rounded-2xl p-6 flex items-start justify-center overflow-auto">
          <div ref={canvasRef} data-testid="canvas"
               className="relative shadow-lg"
               style={{
                 width: tpl.width_mm * px,
                 height: tpl.height_mm * px,
                 background: tpl.background_color,
                 border: "1px dashed #d4d4d4",
               }}>
            {tpl.fields.map((f, i) => {
              const isSel = i === selectedIdx;
              const meta = FIELD_META[f.type] || {};
              const style = {
                left: f.x * px, top: f.y * px,
                width: f.width * px, height: f.height * px,
                fontSize: f.font_size * CANVAS_ZOOM,
                fontWeight: f.bold ? "bold" : "normal",
                fontStyle: f.italic ? "italic" : "normal",
                textAlign: f.align,
                border: (f.border || isSel) ? (isSel ? "2px solid #EB75B5" : "1px solid #666") : "1px dashed #cbd5e1",
                color: "#111",
              };
              const isBc = f.type === "barcode";
              const isLogo = f.type === "logo";
              const preview = f.type === "text"
                ? (f.text || "Freier Text")
                : (meta.sample || meta.label || f.type);
              return (
                <div key={i} data-testid={`field-${i}`}
                     onMouseDown={(e) => onDown(e, i)}
                     className="absolute cursor-move select-none flex items-center overflow-hidden"
                     style={{ ...style, background: isBc || isLogo ? "rgba(255,255,255,0.85)" : "transparent",
                              justifyContent: f.align === "center" ? "center" : f.align === "right" ? "flex-end" : "flex-start",
                              padding: "0 2px" }}
                     title={meta.label || f.type}>
                  {isBc ? (
                    <div className="flex items-center gap-1 opacity-80">
                      {f.barcode_type === "qr"
                        ? <QrCode size={Math.max(10, f.height * px * 0.6)} />
                        : <BarcodeIcon size={Math.max(10, f.height * px * 0.6)} />}
                      <span style={{ fontSize: Math.max(9, f.font_size * CANVAS_ZOOM * 0.7) }}>
                        {(f.barcode_type || "code128").toUpperCase()}
                      </span>
                    </div>
                  ) : isLogo ? (
                    <img
                      src="/logos/logo-black.png"
                      alt="POPCRN Logo"
                      draggable="false"
                      style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain",
                               pointerEvents: "none" }}
                    />
                  ) : (
                    <span className="truncate w-full" style={{ lineHeight: 1.1 }}>{preview}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Properties */}
        <div className="bg-white border border-[var(--border)] rounded-2xl p-3 space-y-3" data-testid="props">
          <div className="overline text-xs">Feld-Eigenschaften</div>
          {!sel && <div className="text-xs text-[var(--text-muted)]">Klicke ein Feld an, um Eigenschaften zu bearbeiten.</div>}
          {sel && (
            <>
              <div>
                <Label className="text-xs">Typ</Label>
                <div className="font-mono text-sm">{sel.type}</div>
              </div>
              {sel.type === "text" && (
                <div>
                  <Label className="text-xs">Text</Label>
                  <Input value={sel.text || ""} onChange={(e) => updateField(selectedIdx, { text: e.target.value })}
                         data-testid="prop-text" />
                </div>
              )}
              {sel.type === "barcode" && (
                <div>
                  <Label className="text-xs">Barcode-Typ</Label>
                  <Select value={sel.barcode_type || "code128"} onValueChange={(v) => updateField(selectedIdx, { barcode_type: v })}>
                    <SelectTrigger data-testid="prop-barcode-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="code128">Code128</SelectItem>
                      <SelectItem value="qr">QR-Code</SelectItem>
                      <SelectItem value="ean13">EAN-13</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">X (mm)</Label>
                  <Input type="number" step="0.5" value={sel.x} data-testid="prop-x"
                         onChange={(e) => updateField(selectedIdx, { x: Number(e.target.value) || 0 })} /></div>
                <div><Label className="text-xs">Y (mm)</Label>
                  <Input type="number" step="0.5" value={sel.y} data-testid="prop-y"
                         onChange={(e) => updateField(selectedIdx, { y: Number(e.target.value) || 0 })} /></div>
                <div><Label className="text-xs">B (mm)</Label>
                  <Input type="number" step="0.5" value={sel.width} data-testid="prop-width"
                         onChange={(e) => updateField(selectedIdx, { width: Number(e.target.value) || 1 })} /></div>
                <div><Label className="text-xs">H (mm)</Label>
                  <Input type="number" step="0.5" value={sel.height} data-testid="prop-height"
                         onChange={(e) => updateField(selectedIdx, { height: Number(e.target.value) || 1 })} /></div>
              </div>
              <div>
                <Label className="text-xs">Schriftgröße</Label>
                <Input type="number" step="0.5" value={sel.font_size} data-testid="prop-fontsize"
                       onChange={(e) => updateField(selectedIdx, { font_size: Number(e.target.value) || 10 })} />
              </div>
              <div className="flex items-center gap-3 text-xs">
                <label className="cursor-pointer inline-flex items-center gap-1">
                  <input type="checkbox" checked={sel.bold} data-testid="prop-bold"
                         onChange={(e) => updateField(selectedIdx, { bold: e.target.checked })} /> Fett
                </label>
                <label className="cursor-pointer inline-flex items-center gap-1">
                  <input type="checkbox" checked={sel.italic} data-testid="prop-italic"
                         onChange={(e) => updateField(selectedIdx, { italic: e.target.checked })} /> Kursiv
                </label>
                <label className="cursor-pointer inline-flex items-center gap-1">
                  <input type="checkbox" checked={sel.border} data-testid="prop-border"
                         onChange={(e) => updateField(selectedIdx, { border: e.target.checked })} /> Rahmen
                </label>
              </div>
              <div>
                <Label className="text-xs">Ausrichtung</Label>
                <Select value={sel.align} onValueChange={(v) => updateField(selectedIdx, { align: v })}>
                  <SelectTrigger data-testid="prop-align"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="left">Links</SelectItem>
                    <SelectItem value="center">Zentriert</SelectItem>
                    <SelectItem value="right">Rechts</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" onClick={() => removeField(selectedIdx)}
                      data-testid="btn-remove-field"
                      className="w-full rounded-full border-red-300 text-red-700 hover:bg-red-50">
                <Trash2 size={14} className="mr-1" /> Feld entfernen
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="mt-4 text-xs text-[var(--text-muted)]">
        ℹ️ Speichere die Vorlage, um sie in Lager · Versand · Produktion beim Drucken auswählen zu können.
        Der PDF-Export erzeugt randlose Seiten exakt in Etikettengröße – ideal für Zebra-/Brother-Labeldrucker.
        <br />
        <span className="opacity-70">Tipp: Beim Ziehen rastet auf 1&nbsp;mm ein. Halte <kbd className="px-1 rounded bg-[var(--pop-cream)]/60 border border-[var(--border)] text-[10px] font-mono">Alt</kbd> gedrückt für pixelgenaues Ziehen.</span>
      </div>
    </div>
  );
}
